import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, XCircle, ArrowUpDown, TrendingDown, TrendingUp, Search, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { SettlementReconTable } from './SettlementReconTable';

const STATUS_CONFIG = {
  matched: { label: 'Matched', icon: CheckCircle, color: 'text-success', bg: 'bg-success/10 border-success/30', badge: 'bg-success/20 text-success' },
  warning: { label: 'Warning', icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10 border-warning/30', badge: 'bg-warning/20 text-warning' },
  critical: { label: 'Critical', icon: XCircle, color: 'text-error', bg: 'bg-error/10 border-error/30', badge: 'bg-error/20 text-error' },
};

const ISSUE_ICONS = {
  commission_overcharged: { icon: TrendingUp, color: 'text-error' },
  commission_undercharged: { icon: TrendingDown, color: 'text-info' },
  underpaid: { icon: TrendingDown, color: 'text-error' },
  overpaid: { icon: TrendingUp, color: 'text-success' },
  unsettled: { icon: AlertTriangle, color: 'text-warning' },
};

export function ReconciliationTab({ branchId = 'all', startDate = '', endDate = '' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedPlatform, setExpandedPlatform] = useState(null);

  const fetchReconciliation = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (branchId && branchId !== 'all') params.append('branch_id', branchId);
      const res = await api.get(`/platforms/reconciliation-dashboard?${params}`);
      setData(res.data);
    } catch {
      toast.error('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  // iter961 — carry the module-wide Branch + Date filters from the page header.
  useEffect(() => { fetchReconciliation(); /* eslint-disable-next-line */ }, [branchId, startDate, endDate]);

  if (!data && loading) return <div className="flex items-center justify-center h-32 text-muted-foreground">Loading reconciliation...</div>;
  if (!data) return null;

  const { platforms, totals } = data;
  const hasIssues = totals.issues_count > 0;

  return (
    <div className="space-y-5" data-testid="reconciliation-tab">
      <SettlementReconTable branchId={branchId} startDate={startDate} endDate={endDate} />
      {/* Filter indicator (controlled by the page header) */}
      <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground" data-testid="recon-filter-indicator">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/60 border border-border px-2.5 py-1 font-medium">
          Period: {startDate || 'All time'} → {endDate || 'Present'}
        </span>
        <Button onClick={fetchReconciliation} disabled={loading} size="sm" variant="outline" className="gap-2 h-8">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={hasIssues ? 'border-error/30 bg-error/10' : 'border-success/30 bg-success/10'}>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Issues Found</div>
            <div className={`text-2xl font-bold ${hasIssues ? 'text-error' : 'text-success'}`}>{totals.issues_count}</div>
          </CardContent>
        </Card>
        <Card className={Math.abs(totals.commission_discrepancy) > 1 ? 'border-warning/30 bg-warning/10' : ''}>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Commission Discrepancy</div>
            <div className={`text-2xl font-bold ${totals.commission_discrepancy > 0 ? 'text-error' : totals.commission_discrepancy < 0 ? 'text-info' : 'text-foreground'}`}>
              {cur()} {Math.abs(totals.commission_discrepancy).toLocaleString()}
            </div>
            {totals.commission_discrepancy > 0 && <div className="text-xs text-error mt-0.5">Overcharged</div>}
            {totals.commission_discrepancy < -1 && <div className="text-xs text-info mt-0.5">Undercharged</div>}
          </CardContent>
        </Card>
        <Card className={totals.payment_discrepancy < -1 ? 'border-error/30 bg-error/10' : ''}>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Payment Discrepancy</div>
            <div className={`text-2xl font-bold ${totals.payment_discrepancy < -1 ? 'text-error' : totals.payment_discrepancy > 1 ? 'text-success' : 'text-foreground'}`}>
              {cur()} {Math.abs(totals.payment_discrepancy).toLocaleString()}
            </div>
            {totals.payment_discrepancy < -1 && <div className="text-xs text-error mt-0.5">Underpaid</div>}
            {totals.payment_discrepancy > 1 && <div className="text-xs text-success mt-0.5">Overpaid</div>}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Total Platform Sales</div>
            <div className="text-2xl font-bold text-foreground">{cur()} {totals.total_sales.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Platform Cards */}
      <div className="space-y-3">
        {platforms.map(plat => {
          const cfg = STATUS_CONFIG[plat.status] || STATUS_CONFIG.matched;
          const StatusIcon = cfg.icon;
          const isExpanded = expandedPlatform === plat.platform_id;

          return (
            <Card key={plat.platform_id} className={`${cfg.bg} transition-all`} data-testid={`recon-platform-${plat.platform_id}`}>
              <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpandedPlatform(isExpanded ? null : plat.platform_id)}>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <StatusIcon size={20} className={cfg.color} />
                    <span className="font-outfit text-base">{plat.platform_name}</span>
                    <Badge className={`text-xs ${cfg.badge}`}>{cfg.label}</Badge>
                    <span className="text-xs text-muted-foreground">{plat.commission_rate}% rate</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span>Sales: {cur()} {plat.total_sales.toLocaleString()}</span>
                    <span className="text-success">Received: {cur()} {plat.actual_received.toLocaleString()}</span>
                    <ArrowUpDown size={14} className="text-muted-foreground" />
                  </div>
                </CardTitle>
              </CardHeader>

              {/* Issues */}
              {plat.issues.length > 0 && (
                <CardContent className="pt-0 pb-2">
                  <div className="flex flex-wrap gap-2">
                    {plat.issues.map((issue, idx) => {
                      const ic = ISSUE_ICONS[issue.type] || ISSUE_ICONS.unsettled;
                      const IssueIcon = ic.icon;
                      return (
                        <div key={idx} className="flex items-center gap-1.5 text-xs bg-white/80 dark:bg-muted/50 rounded-lg px-2.5 py-1.5 border">
                          <IssueIcon size={12} className={ic.color} />
                          <span>{issue.message}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}

              {/* Expanded: Commission & Payment comparison */}
              {isExpanded && (
                <CardContent className="pt-0 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Commission Analysis */}
                    <div className="p-3 rounded-lg bg-white/70 dark:bg-muted/50 border">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Commission Analysis</h4>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Expected ({plat.commission_rate}%):</span>
                          <span className="font-medium">{cur()} {plat.expected_commission.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Actual Charged:</span>
                          <span className="font-medium">{cur()} {plat.actual_commission.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between border-t pt-1.5">
                          <span className="font-medium">Difference:</span>
                          <span className={`font-bold ${plat.commission_discrepancy > 0 ? 'text-error' : plat.commission_discrepancy < 0 ? 'text-info' : 'text-success'}`}>
                            {plat.commission_discrepancy > 0 ? '+' : ''}SR {plat.commission_discrepancy.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Analysis */}
                    <div className="p-3 rounded-lg bg-white/70 dark:bg-muted/50 border">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Payment Analysis</h4>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Expected Received:</span>
                          <span className="font-medium">{cur()} {plat.expected_received.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Actual Received:</span>
                          <span className="font-medium">{cur()} {plat.actual_received.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between border-t pt-1.5">
                          <span className="font-medium">Difference:</span>
                          <span className={`font-bold ${plat.payment_discrepancy < 0 ? 'text-error' : plat.payment_discrepancy > 0 ? 'text-success' : 'text-success'}`}>
                            {plat.payment_discrepancy > 0 ? '+' : ''}SR {plat.payment_discrepancy.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Per-Payment Breakdown */}
                  {plat.payment_details.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Payment-by-Payment Breakdown</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-white/50 dark:bg-muted/30">
                            <tr>
                              <th className="text-left p-2">Date</th>
                              <th className="text-left p-2">Period</th>
                              <th className="text-right p-2">Sales</th>
                              <th className="text-right p-2">Exp. Comm.</th>
                              <th className="text-right p-2">Act. Comm.</th>
                              <th className="text-right p-2">Comm. Diff</th>
                              <th className="text-right p-2">Exp. Recv.</th>
                              <th className="text-right p-2">Act. Recv.</th>
                              <th className="text-right p-2">Pay. Diff</th>
                              <th className="text-center p-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {plat.payment_details.map((pd, idx) => (
                              <tr key={idx} className="border-t border-white/50">
                                <td className="p-2">{pd.payment_date}</td>
                                <td className="p-2 text-muted-foreground">{pd.period}</td>
                                <td className="p-2 text-right">{cur()} {pd.total_sales.toLocaleString()}</td>
                                <td className="p-2 text-right">{cur()} {pd.expected_commission.toLocaleString()}</td>
                                <td className="p-2 text-right">{cur()} {pd.actual_commission.toLocaleString()}</td>
                                <td className={`p-2 text-right font-medium ${pd.commission_diff > 0 ? 'text-error' : pd.commission_diff < 0 ? 'text-info' : ''}`}>
                                  {pd.commission_diff !== 0 ? `${pd.commission_diff > 0 ? '+' : ''}${pd.commission_diff}` : '-'}
                                </td>
                                <td className="p-2 text-right">{cur()} {pd.expected_received.toLocaleString()}</td>
                                <td className="p-2 text-right">{cur()} {pd.actual_received.toLocaleString()}</td>
                                <td className={`p-2 text-right font-medium ${pd.payment_diff < 0 ? 'text-error' : pd.payment_diff > 0 ? 'text-success' : ''}`}>
                                  {pd.payment_diff !== 0 ? `${pd.payment_diff > 0 ? '+' : ''}${pd.payment_diff}` : '-'}
                                </td>
                                <td className="p-2 text-center">
                                  {pd.status === 'matched'
                                    ? <CheckCircle size={14} className="text-success inline" />
                                    : <AlertTriangle size={14} className="text-warning inline" />}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {plat.pending_sales > 0 && (
                    <div className="text-xs text-warning bg-warning/10 p-2 rounded border border-warning/30">
                      {cur()} {plat.pending_sales.toLocaleString()} in unsettled sales not yet included in any payment
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}

        {platforms.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <Search size={48} className="mx-auto mb-3 opacity-30" />
              <p>No platform data to reconcile</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
